import { Fragment } from 'react';
import { TURNSTILE_ERROR_SCRIPT, TURNSTILE_SCRIPT_SRC } from './blocks/SpamGuardFields';
import type { ReactNode } from 'react';
import type { Block, BlockData } from './types';
import { sectionAnchorIds } from './sectionAnchors';
import { HeroBlock } from './blocks/Hero';
import { RichTextBlock } from './blocks/RichText';
import { ListingsBlock } from './blocks/Listings';
import { listingsFeedKey } from './listingsFeed';
import { AgentsBlock } from './blocks/Agents';
import { OfficesBlock } from './blocks/Offices';
import { CommunitiesBlock } from './blocks/Communities';
import { BlogListBlock } from './blocks/BlogList';
import { ContactFormBlock } from './blocks/ContactForm';
import { HomeValuationBlock } from './blocks/HomeValuation';
import { SearchAlertBlock } from './blocks/SearchAlert';
import { NetProceedsBlock } from './blocks/NetProceeds';
import { SellVsRentBlock } from './blocks/SellVsRent';
import { CommunityMatchBlock } from './blocks/CommunityMatch';
import { NewsletterMastheadBlock } from './blocks/NewsletterMasthead';
import { IssueLayoutBlock } from './blocks/IssueLayout';
import { SpotlightCardBlock } from './blocks/SpotlightCard';
import { EventsListBlock } from './blocks/EventsList';
import { ImageBlock } from './blocks/Image';
import { CtaBlock } from './blocks/Cta';
import { RawHtmlBlock } from './blocks/RawHtml';
import { NavBlock } from './blocks/Nav';
import { SectionMenuBlock } from './blocks/SectionMenu';
import { SearchBlock } from './blocks/Search';
import { FooterBlock } from './blocks/Footer';
import { MapBlock } from './blocks/Map';
import { StatsBlock } from './blocks/Stats';
import { FeatureGridBlock } from './blocks/FeatureGrid';
import { TestimonialsBlock } from './blocks/Testimonials';
import { FaqBlock } from './blocks/Faq';
import { TableBlock } from './blocks/Table';
import { ColumnsBlock } from './blocks/Columns';
import { LogoCarouselBlock } from './blocks/LogoCarousel';
import { SearchResultsBlock } from './blocks/SearchResults';
import { AgentProfileBlock } from './blocks/AgentProfile';
import { ContactDetailsBlock } from './blocks/ContactDetails';
import { AutomaticContentBlock } from './blocks/AutomaticContent';

/** Render a single block. Hidden blocks and unknown/future types render nothing. */
export function renderBlock(block: Block, data?: BlockData): ReactNode {
  if (block.hidden) return null;
  switch (block.type) {
    case 'nav': return <NavBlock {...block.props} />;
    case 'section_menu': return <SectionMenuBlock {...block.props} />;
    case 'hero': return <HeroBlock {...block.props} previewStatic={data?.previewMode} />;
    case 'search': return <SearchBlock {...block.props} />;
    case 'rich_text': return <RichTextBlock {...block.props} />;
    case 'listings': {
      const feed = data?.listingsByFeed?.[listingsFeedKey(block.props)];
      return <ListingsBlock props={block.props} listings={feed ? feed.listings : data?.listings ?? []} isSample={feed ? feed.isSample : data?.listingsAreSample} previewMode={data?.previewMode} />;
    }
    case 'agents': return <AgentsBlock props={block.props} agents={data?.agents ?? []} />;
    case 'offices': return <OfficesBlock props={block.props} offices={data?.offices ?? []} />;
    case 'communities': return <CommunitiesBlock props={block.props} communities={data?.communities ?? []} />;
    case 'blog_list': return <BlogListBlock props={block.props} posts={data?.blogPosts ?? []} />;
    case 'stats': return <StatsBlock {...block.props} />;
    case 'feature_grid': return <FeatureGridBlock {...block.props} />;
    case 'testimonials': return <TestimonialsBlock {...block.props} reviews={data?.reviews} />;
    case 'faq': return <FaqBlock {...block.props} />;
    case 'table': return <TableBlock {...block.props} />;
    case 'columns': return <ColumnsBlock {...block.props} />;
    case 'logo_carousel': return <LogoCarouselBlock {...block.props} />;
    case 'search_results': return <SearchResultsBlock {...block.props} listings={data?.listings ?? []} mapboxToken={data?.mapboxToken} />;
    case 'contact_form': return <ContactFormBlock {...block.props} submitState={data?.contactSubmit} turnstileSiteKey={data?.turnstileSiteKey} />;
    case 'home-valuation': return <HomeValuationBlock {...block.props} turnstileSiteKey={data?.turnstileSiteKey} />;
    case 'search-alert': return <SearchAlertBlock {...block.props} turnstileSiteKey={data?.turnstileSiteKey} />;
    case 'net-proceeds': return <NetProceedsBlock {...block.props} turnstileSiteKey={data?.turnstileSiteKey} />;
    case 'sell-vs-rent': return <SellVsRentBlock {...block.props} turnstileSiteKey={data?.turnstileSiteKey} />;
    case 'community-match': return <CommunityMatchBlock {...block.props} turnstileSiteKey={data?.turnstileSiteKey} />;
    case 'newsletter-masthead': return <NewsletterMastheadBlock {...block.props} issues={data?.newsletterIssues ?? []} publisher={data?.newsletterPublisher ?? null} />;
    case 'issue-layout': return <IssueLayoutBlock {...block.props} issues={data?.newsletterIssues ?? []} />;
    case 'spotlight-card': return <SpotlightCardBlock {...block.props} spotlight={data?.spotlight ?? null} issues={data?.newsletterIssues ?? []} />;
    case 'events-list': return <EventsListBlock {...block.props} events={data?.events ?? null} eventsCommunitySlug={data?.eventsCommunitySlug ?? null} issues={data?.newsletterIssues ?? []} />;
    case 'image': return <ImageBlock {...block.props} />;
    case 'cta': return <CtaBlock {...block.props} />;
    case 'footer': return <FooterBlock {...block.props} />;
    case 'map': return <MapBlock {...block.props} listings={data?.listings ?? []} mapboxToken={data?.mapboxToken} />;
    case 'raw_html': return <RawHtmlBlock {...block.props} />;
    case 'agent_profile': return <AgentProfileBlock props={block.props} profile={data?.profile} companyName={data?.companyName} />;
    case 'contact_details': return <ContactDetailsBlock props={block.props} profile={data?.profile} companyName={data?.companyName} />;
    case 'automatic_content': return <AutomaticContentBlock {...block.props} content={data?.automaticContent} previewMode={data?.previewMode} placeholder={data?.automaticContentPlaceholder} />;
    default: return null; // unknown/future block type — skip gracefully (forward-compatible)
  }
}

export interface BlockRendererProps {
  blocks: Block[];
  data?: BlockData;
}

/** Module-scope so the object identity is stable and the wrapper never re-renders on it. */
const DISPLAY_CONTENTS = { display: 'contents' } as const;

/**
 * Renders an ordered list of blocks. PURE: it takes data, returns markup, and
 * knows nothing about routing, auth, or data fetching — that's the host's job.
 * No wrapper chrome (the ./editor entrypoint adds selection hooks instead).
 */
export function BlockRenderer({ blocks, data }: BlockRendererProps): ReactNode {
  const anchors = sectionAnchorIds(blocks);
  return (
    <>
      {blocks.map((block, i) => (
        // `data-block-type` is a TEST HOOK: the e2e suite reads section order and count off the
        // rendered page, and the alternative is matching class names that change whenever anyone
        // touches the styling.
        //
        // `display: contents` is what makes it safe to add. A normal wrapper div would generate a
        // box, and the nav renders `position: sticky` — a sticky element only sticks within its
        // containing block, so a wrapper exactly the nav's height stops it sticking at all. That
        // matters here specifically because the Studio preview renders nav and footer THROUGH
        // this component (the tenant pages render them in the layout instead), and the preview
        // exists to show sticky behaviour faithfully. `display: contents` generates no box, so
        // the block's own element lays out exactly as if this div were not here — including as a
        // flex/grid item — while staying in the DOM to be queried.
        <div
          key={block.id}
          data-block-type={block.type}
          data-block-id={block.id}
          // Section anchor (sectionAnchors.ts): the id a Section menu jumps to, unique on the page.
          id={anchors[i]}
          style={DISPLAY_CONTENTS}
        >
          {renderBlock(block, data)}
        </div>
      ))}
      {/* One Turnstile loader for every contact form on the page (SpamGuardFields). */}
      {data?.turnstileSiteKey && blocks.some((b) => b.type === 'contact_form') ? (
        <>
          <script dangerouslySetInnerHTML={{ __html: TURNSTILE_ERROR_SCRIPT }} />
          <script src={TURNSTILE_SCRIPT_SRC} async defer />
        </>
      ) : null}
    </>
  );
}
